import { useFlippers } from "@shared/context";
import { Navigate, Route, Routes, useLocation } from "@shared/react-router-dom";
import { wildcardPath } from "@shared/utils";
import { authRoutes } from "@unauthenticated/shared/data/authRoutes";
import type { PatientAttributes } from "@unauthenticated/signupSignin/data/types";
import type { LocationType } from "@unauthenticated/signupSignin/data/types/PatientAuth";
import {
  PatientAuthPasswordPage,
  PatientAuthStartPage,
  PatientRxSignInPage,
  PatientStoreActivationPage,
  PatientStoreSignUpPage,
} from "@unauthenticated/signupSignin/scenes";
import { ContinueWithoutPasswordPage } from "@unauthenticated/signupSignin/scenes/ContinueWithoutPasswordPage";
import { ForgotPasswordPage } from "@unauthenticated/signupSignin/scenes/ForgotPasswordPage/ForgotPasswordPage";
import { PatientIntakePage } from "@unauthenticated/signupSignin/scenes/PatientIntakePage";

interface Props {
  csuid: string;
  patientAttributes: PatientAttributes;
  additionalAttributes?: any;
  ssoErrors?: string;
  pageVersion?: "react_with_sso" | "react";
  isPreview?: boolean;
  isStoreOwner: boolean;
  storeId?: string;
}

const Pages = ({
  csuid,
  patientAttributes,
  additionalAttributes,
  ssoErrors,
  pageVersion,
  isPreview,
  isStoreOwner,
  storeId,
}: Props) => {
  const { storeSlug, treatmentPlanSlug, uuid, isActivated } = patientAttributes;
  const identitySlug = uuid || treatmentPlanSlug;
  const [isSupplementPlan, isSwiftFillEnabled] = useFlippers(
    "dynamo_supplement_plan",
    "titan_swift_fill"
  );

  const { search: searchParams, state }: LocationType = useLocation();

  const getRoutes = () => {
    if (!uuid) {
      if (isSwiftFillEnabled) {
        return `${authRoutes.welcome_start(storeSlug, identitySlug)}${searchParams}`;
      } else {
        return `${authRoutes.welcome_sign_up(storeSlug)}${searchParams}`;
      }
    }
    if (isActivated) {
      return `${authRoutes.welcome_sign_in(storeSlug, identitySlug)}${searchParams}`;
    }

    return `${authRoutes.welcome_activation(storeSlug, identitySlug)}${searchParams}`;
  };

  const getPatientAuthPasswordElement = () => {
    if (!state?.email) {
      return (
        <Navigate to={`${authRoutes.welcome_start(storeSlug, identitySlug)}${searchParams}`} />
      );
    }

    return (
      <PatientAuthPasswordPage
        patientAttributes={patientAttributes}
        storeId={storeId}
        additionalAttributes={additionalAttributes}
      />
    );
  };

  const getPatientSignInElement = () => {
    if (isSwiftFillEnabled) {
      // In case there is an email already in the state, we want to redirect to the password page,
      // If there isn't the getPatientAuthPasswordElement will redirect to the start page
      return getPatientAuthPasswordElement();
    }

    return (
      <PatientRxSignInPage
        patientAttributes={patientAttributes}
        additionalAttributes={additionalAttributes}
        ssoErrors={ssoErrors}
        storeId={storeId}
      />
    );
  };
  const getPatientSignupElement = () => {
    if (!state?.email && isSwiftFillEnabled) {
      return (
        <Navigate to={`${authRoutes.welcome_start(storeSlug, identitySlug)}${searchParams}`} />
      );
    }

    return (
      <PatientStoreSignUpPage
        storeId={storeId}
        pageVersion={pageVersion}
        patientAttributes={patientAttributes}
        additionalAttributes={additionalAttributes}
        isPreview={isPreview}
        isStoreOwner={isStoreOwner}
        ssoErrors={ssoErrors}
      />
    );
  };

  return (
    <Routes>
      <Route
        path={wildcardPath(authRoutes.welcome_sign_up(storeSlug))}
        element={getPatientSignupElement()}
      />
      {isSupplementPlan && (
        <Route
          path={wildcardPath(authRoutes.intake_sign_up(storeSlug))}
          element={
            <PatientIntakePage
              isPreview={isPreview}
              isStoreOwner={isStoreOwner}
              patientAttributes={patientAttributes}
            />
          }
        />
      )}
      <Route
        path={wildcardPath(authRoutes.welcome_sign_in(storeSlug, identitySlug))}
        element={getPatientSignInElement()}
      />
      {isSwiftFillEnabled && (
        <>
          <Route
            path={wildcardPath(authRoutes.welcome_start(storeSlug, identitySlug))}
            element={
              <PatientAuthStartPage
                patientAttributes={patientAttributes}
                additionalAttributes={additionalAttributes}
                storeId={storeId}
                isPreview={isPreview}
                isStoreOwner={isStoreOwner}
              />
            }
          />
          <Route
            path={wildcardPath(authRoutes.welcome_password(storeSlug, identitySlug))}
            element={getPatientAuthPasswordElement()}
          />
        </>
      )}
      <Route
        path={wildcardPath(authRoutes.welcome_activation(storeSlug, identitySlug))}
        element={
          <PatientStoreActivationPage
            patientAttributes={patientAttributes}
            additionalAttributes={additionalAttributes}
            csuid={`${csuid}`}
            pageVersion={pageVersion}
            ssoErrors={ssoErrors}
            storeId={storeId}
          />
        }
      />
      <Route
        path={wildcardPath(authRoutes.welcome_forgot_password(storeSlug, identitySlug))}
        element={<ForgotPasswordPage />}
      />
      <Route
        path={wildcardPath(authRoutes.continue_without_password)}
        element={<ContinueWithoutPasswordPage />}
      />
      <Route path="*" element={<Navigate to={getRoutes()} replace />} />
    </Routes>
  );
};

export { Pages };
